import React, { useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from '@mui/material';

export type SessionReplacementProofType = 'password' | 'parentPhone' | 'oauth';
export type SessionReplacementProof = { password?: string; parentPhone?: string };

interface SessionReplacementDialogProps {
  open: boolean;
  proofType: SessionReplacementProofType;
  loading: boolean;
  error: string | null;
  onConfirm: (proof: SessionReplacementProof) => void;
  onCancel: () => void;
}

const SessionReplacementDialog: React.FC<SessionReplacementDialogProps> = ({
  open,
  proofType,
  loading,
  error,
  onConfirm,
  onCancel,
}) => {
  const [credential, setCredential] = useState('');

  useEffect(() => {
    if (open) setCredential('');
  }, [open, proofType]);

  const needsCredential = proofType !== 'oauth';
  const isParentPhone = proofType === 'parentPhone';

  const confirm = () => {
    if (isParentPhone) onConfirm({ parentPhone: credential.trim() });
    else if (proofType === 'password') onConfirm({ password: credential });
    else onConfirm({});
  };

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="xs">
      <DialogTitle>Sign in on this device?</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          This account may have an existing sign-in. Continuing will make this device active and sign out any previous session.
        </DialogContentText>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {needsCredential ? (
          <TextField
            autoFocus
            fullWidth
            required
            label={isParentPhone ? 'Parent phone number' : 'Password'}
            type={isParentPhone ? 'tel' : 'password'}
            autoComplete={isParentPhone ? 'tel' : 'current-password'}
            value={credential}
            onChange={(event) => setCredential(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && credential.trim()) confirm();
            }}
          />
        ) : (
          <Alert severity="info">Your Google sign-in has verified your identity. Confirm to sign out previous sessions.</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>Cancel</Button>
        <Button onClick={confirm} variant="contained" disabled={loading || (needsCredential && !credential.trim())}>
          {loading ? 'Verifying...' : 'Sign out other devices'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionReplacementDialog;